import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import apiClient from "@/lib/api";
import { AmenityIcon, groupByCategory } from "@/components/property/amenityMeta";

/**
 * Amenity multi-select (chips grouped by category).
 * Controlled: `selected` = array of amenity_id, `onChange(ids)` updates it.
 */
export default function AmenitySelector({ selected = [], onChange, category = null }) {
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    apiClient
      .get("/amenities", { params: category ? { category } : {} })
      .then((r) => active && setAmenities(r.data || []))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [category]);

  const toggle = (id) => {
    const set = new Set(selected);
    set.has(id) ? set.delete(id) : set.add(id);
    onChange(Array.from(set));
  };

  if (loading) {
    return (
      <div className="py-6 grid place-items-center" data-testid="amenity-selector-loading">
        <Loader2 className="animate-spin text-primary" size={22} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive" data-testid="amenity-selector-error">
        Gagal memuat daftar fasilitas.
      </div>
    );
  }

  const groups = groupByCategory(amenities);

  return (
    <div className="space-y-5" data-testid="amenity-selector">
      {groups.map((g) => (
        <div key={g.category}>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">{g.label}</p>
          <div className="flex flex-wrap gap-2">
            {g.items.map((a) => {
              const on = selected.includes(a.id);
              return (
                <button
                  type="button"
                  key={a.id}
                  onClick={() => toggle(a.id)}
                  aria-pressed={on}
                  data-testid={`amenity-chip-${a.slug}`}
                  data-selected={on}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                    on
                      ? "border-accent bg-accent text-accent-foreground"
                      : "border-border bg-secondary/40 text-foreground hover:bg-secondary/70"
                  }`}
                >
                  <AmenityIcon name={a.icon} size={15} />
                  {a.name}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
